import { useState } from 'react';
import { Lock, User, Eye, EyeOff, Mail, ArrowLeft, CheckCircle, Shield } from 'lucide-react';

type Mode = 'login' | 'reset';

/* ── Orb SVG logo — inline pentru a fi sigur că se încarcă ── */
function OrbLogo({ size = 44 }: { size?: number }) {
  return (
    <svg width={size} height={size} viewBox="0 0 44 44" fill="none" xmlns="http://www.w3.org/2000/svg">
      <defs>
        <radialGradient id="lg-aura" cx="50%" cy="50%" r="50%">
          <stop offset="0%" stopColor="#00e5ff" stopOpacity="0.85"/>
          <stop offset="42%" stopColor="#4ab8ff" stopOpacity="0.6"/>
          <stop offset="78%" stopColor="#a855f7" stopOpacity="0.35"/>
          <stop offset="100%" stopColor="#ec4899" stopOpacity="0"/>
        </radialGradient>
        <radialGradient id="lg-core" cx="42%" cy="38%" r="50%">
          <stop offset="0%" stopColor="#e0f7fa"/>
          <stop offset="55%" stopColor="#00e5ff"/>
          <stop offset="100%" stopColor="#0ea5e9"/>
        </radialGradient>
        <linearGradient id="lg-ring" x1="0%" y1="50%" x2="100%" y2="50%">
          <stop offset="0%" stopColor="#00e5ff" stopOpacity="0.95"/>
          <stop offset="100%" stopColor="#ec4899" stopOpacity="0.9"/>
        </linearGradient>
        <filter id="lg-glow">
          <feGaussianBlur stdDeviation="2.5" result="b"/>
          <feMerge><feMergeNode in="b"/><feMergeNode in="SourceGraphic"/></feMerge>
        </filter>
        <filter id="lg-sglow">
          <feGaussianBlur stdDeviation="1.5" result="b"/>
          <feMerge><feMergeNode in="b"/><feMergeNode in="SourceGraphic"/></feMerge>
        </filter>
      </defs>
      {/* Aura */}
      <ellipse cx="22" cy="22" rx="20" ry="20" fill="url(#lg-aura)" opacity="0.6"/>
      {/* Ring gradient */}
      <circle cx="22" cy="22" r="14" fill="none" stroke="url(#lg-ring)" strokeWidth="1.8" opacity="0.9" filter="url(#lg-sglow)"/>
      {/* Inner ring */}
      <circle cx="22" cy="22" r="9.5" fill="none" stroke="#4ae3ff" strokeWidth="0.9" opacity="0.55"/>
      {/* Core orb */}
      <circle cx="22" cy="22" r="6.5" fill="url(#lg-core)" filter="url(#lg-glow)"/>
      {/* Specular */}
      <ellipse cx="20" cy="19.5" rx="2.8" ry="1.8" fill="white" opacity="0.7"/>
      {/* Satellites */}
      <circle cx="33.5" cy="9.5" r="2.2" fill="#00e5ff" opacity="0.85" filter="url(#lg-sglow)"/>
      <circle cx="34.5" cy="35" r="1.6" fill="#ec4899" opacity="0.75"/>
      {/* Orbit lines */}
      <line x1="28.5" y1="14" x2="32.5" y2="10.5" stroke="#4ab8ff" strokeWidth="0.7" opacity="0.5"/>
      <line x1="29.5" y1="30.5" x2="33.5" y2="34" stroke="#a855f7" strokeWidth="0.7" opacity="0.45"/>
    </svg>
  );
}

/* ── Particule cosmice de fundal ── */
function CosmicBg() {
  const dots = Array.from({ length: 55 }, (_, i) => ({
    x: (Math.sin(i * 137.5 * Math.PI / 180) * 0.5 + 0.5) * 100,
    y: (Math.cos(i * 97.3 * Math.PI / 180) * 0.5 + 0.5) * 100,
    r: [0.8, 1.1, 1.4, 0.6][i % 4],
    op: [0.12, 0.22, 0.16, 0.09, 0.18][i % 5],
  }));
  return (
    <svg className="absolute inset-0 w-full h-full pointer-events-none" xmlns="http://www.w3.org/2000/svg">
      {dots.map((d, i) => (
        <circle key={i} cx={`${d.x}%`} cy={`${d.y}%`} r={d.r} fill="#4ab8ff" opacity={d.op}/>
      ))}
      {/* Connecting lines (subtle network) */}
      {[
        [8, 19], [19, 32], [32, 45], [3, 14], [14, 27], [27, 40], [5, 22], [22, 38],
      ].map(([a, b], i) => (
        <line key={i}
          x1={`${dots[a].x}%`} y1={`${dots[a].y}%`}
          x2={`${dots[b].x}%`} y2={`${dots[b].y}%`}
          stroke="#295CA8" strokeWidth="0.5" opacity="0.18"/>
      ))}
    </svg>
  );
}

export default function LoginPage({ onLogin }: { onLogin: (username: string, password: string) => boolean }) {
  const [mode, setMode]           = useState<Mode>('login');
  const [username, setUsername]   = useState('');
  const [password, setPassword]   = useState('');
  const [resetEmail, setResetEmail] = useState('');
  const [showPass, setShowPass]   = useState(false);
  const [error, setError]         = useState('');
  const [success, setSuccess]     = useState('');
  const [loading, setLoading]     = useState(false);

  const handleLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(''); setLoading(true);
    await new Promise(r => setTimeout(r, 200));
    const ok = onLogin(username, password);
    setLoading(false);
    if (!ok) setError('Credențiale incorecte. Verificați username și parola.');
  };

  const handleReset = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(''); setLoading(true);
    try {
      const allowed = ['office@interpersonal.ro', 'calinpredu@gmail.com'];
      if (!allowed.includes(resetEmail.toLowerCase().trim())) {
        setError('Adresă de email neautorizată pentru reset admin.');
        setLoading(false); return;
      }
      const res = await fetch('/auth.php?action=admin-reset-request', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ action: 'admin-reset-request', email: resetEmail }),
      });
      const data = await res.json();
      if (data.ok) setSuccess(data.message || 'Instrucțiunile au fost trimise pe email.');
      else setError(data.error || 'Eroare la trimitere.');
    } catch {
      setError('Eroare de rețea. Verificați conexiunea.');
    }
    setLoading(false);
  };

  const Input = (props: React.InputHTMLAttributes<HTMLInputElement>) => (
    <input
      {...props}
      className="w-full bg-white/5 border border-white/10 rounded-xl pl-10 pr-4 py-3 text-sm text-white placeholder-white/25
        focus:outline-none focus:border-[#295CA8] focus:bg-white/7 transition-all"
    />
  );

  return (
    <div className="min-h-screen relative overflow-hidden flex items-center justify-center p-4"
      style={{ background: 'radial-gradient(ellipse 120% 100% at 50% 0%, #0d1528 0%, #0a0c12 45%, #070910 100%)' }}>

      {/* Cosmic background */}
      <CosmicBg />

      {/* Gradient orbs de fundal */}
      <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[600px] h-[300px] rounded-full pointer-events-none"
        style={{ background: 'radial-gradient(ellipse, rgba(41,92,168,0.18) 0%, transparent 70%)', filter: 'blur(40px)' }}/>
      <div className="absolute bottom-0 right-0 w-80 h-80 rounded-full pointer-events-none"
        style={{ background: 'radial-gradient(ellipse, rgba(168,85,247,0.08) 0%, transparent 70%)', filter: 'blur(50px)' }}/>
      <div className="absolute bottom-1/3 left-0 w-64 h-64 rounded-full pointer-events-none"
        style={{ background: 'radial-gradient(ellipse, rgba(0,229,255,0.07) 0%, transparent 70%)', filter: 'blur(40px)' }}/>

      {/* Card */}
      <div className="relative z-10 w-full max-w-[400px]">

        {/* Branding top */}
        <div className="text-center mb-8">
          <div className="flex flex-col items-center gap-3 mb-5">
            {/* Orb 3D — favicon/icon */}
            <img src="/orb.webp" alt="Orb" className="w-16 h-16 rounded-full object-cover"
              style={{ boxShadow: '0 0 30px rgba(74,184,255,0.5), 0 0 60px rgba(41,92,168,0.3)', border: '2px solid rgba(74,184,255,0.35)' }} />
            {/* Logo text real */}
            <img src="/logo.png" alt="INTERPERSONAL"
              className="h-7 w-auto object-contain"
              style={{ filter: 'brightness(1.4) drop-shadow(0 0 10px rgba(74,184,255,0.4))' }} />
            <div className="text-[10px] text-white/35 tracking-[0.12em] font-medium">ADMIN PANEL · CMS</div>
          </div>

          <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full text-xs text-white/45 border border-white/8"
            style={{ background: 'rgba(41,92,168,0.08)' }}>
            <Shield size={11} className="text-[#4ab8ff]" />
            {mode === 'login' ? 'Acces securizat · Panou administrare' : 'Resetare parolă administrator'}
          </div>
        </div>

        {/* Form card */}
        <div className="rounded-2xl p-7 space-y-5 relative overflow-hidden"
          style={{
            background: 'rgba(7,9,15,0.85)',
            border: '1px solid rgba(41,92,168,0.25)',
            backdropFilter: 'blur(24px)',
            boxShadow: '0 25px 60px rgba(0,0,0,0.5), 0 0 0 1px rgba(74,184,255,0.05) inset',
          }}>

          {/* Top highlight line */}
          <div className="absolute top-0 left-1/2 -translate-x-1/2 w-2/3 h-px"
            style={{ background: 'linear-gradient(90deg, transparent, rgba(74,184,255,0.4), transparent)' }}/>

          {/* ── LOGIN ── */}
          {mode === 'login' && (
            <form onSubmit={handleLogin} className="space-y-4">
              <div>
                <label className="block text-[11px] text-white/45 mb-2 font-semibold uppercase tracking-widest">Utilizator</label>
                <div className="relative">
                  <User size={15} className="absolute left-3.5 top-1/2 -translate-y-1/2 text-white/30 pointer-events-none" />
                  <Input type="text" value={username} onChange={e => setUsername(e.target.value)}
                    placeholder="admin" autoComplete="username" required />
                </div>
              </div>

              <div>
                <label className="block text-[11px] text-white/45 mb-2 font-semibold uppercase tracking-widest">Parolă</label>
                <div className="relative">
                  <Lock size={15} className="absolute left-3.5 top-1/2 -translate-y-1/2 text-white/30 pointer-events-none" />
                  <input type={showPass ? 'text' : 'password'} value={password} onChange={e => setPassword(e.target.value)}
                    className="w-full bg-white/5 border border-white/10 rounded-xl pl-10 pr-10 py-3 text-sm text-white placeholder-white/25 focus:outline-none focus:border-[#295CA8] focus:bg-white/7 transition-all"
                    placeholder="••••••••" autoComplete="current-password" required />
                  <button type="button" onClick={() => setShowPass(s => !s)}
                    className="absolute right-3.5 top-1/2 -translate-y-1/2 text-white/30 hover:text-white/60 transition-colors">
                    {showPass ? <EyeOff size={15} /> : <Eye size={15} />}
                  </button>
                </div>
              </div>

              {error && (
                <div className="text-xs text-red-400 bg-red-500/10 border border-red-500/20 rounded-xl px-4 py-2.5 flex items-center gap-2">
                  <span className="text-red-400">⚠</span> {error}
                </div>
              )}

              <button type="submit" disabled={loading}
                className="w-full py-3 rounded-xl text-sm font-semibold transition-all disabled:opacity-50 disabled:cursor-not-allowed mt-1 relative overflow-hidden"
                style={{ background: 'linear-gradient(135deg, #295CA8 0%, #3D72B8 50%, #295CA8 100%)', boxShadow: '0 0 20px rgba(41,92,168,0.4)' }}>
                <span className="relative z-10">{loading ? 'Se autentifică...' : 'Autentificare'}</span>
              </button>

              <div className="text-center pt-1">
                <button type="button" onClick={() => { setMode('reset'); setError(''); }}
                  className="text-xs text-white/30 hover:text-[#4ab8ff] transition-colors">
                  Ai uitat parola? Resetează
                </button>
              </div>
            </form>
          )}

          {/* ── RESET ── */}
          {mode === 'reset' && (
            <form onSubmit={handleReset} className="space-y-4">
              <p className="text-xs text-white/40 leading-relaxed">
                Introduceți adresa de email autorizată (<strong className="text-white/60">office@interpersonal.ro</strong> sau <strong className="text-white/60">calinpredu@gmail.com</strong>).
                Veți primi un link de resetare valabil 1 oră.
              </p>

              {success ? (
                <div className="flex items-start gap-2 text-xs text-green-400 bg-green-500/10 border border-green-500/20 rounded-xl px-4 py-3">
                  <CheckCircle size={14} className="flex-shrink-0 mt-0.5" /> {success}
                </div>
              ) : (
                <>
                  <div>
                    <label className="block text-[11px] text-white/45 mb-2 font-semibold uppercase tracking-widest">Email admin</label>
                    <div className="relative">
                      <Mail size={15} className="absolute left-3.5 top-1/2 -translate-y-1/2 text-white/30 pointer-events-none" />
                      <Input type="email" value={resetEmail} onChange={e => setResetEmail(e.target.value)}
                        placeholder="office@interpersonal.ro" required />
                    </div>
                  </div>
                  {error && (
                    <div className="text-xs text-red-400 bg-red-500/10 border border-red-500/20 rounded-xl px-4 py-2.5">⚠ {error}</div>
                  )}
                  <button type="submit" disabled={loading}
                    className="w-full py-3 rounded-xl text-sm font-semibold transition-all disabled:opacity-50"
                    style={{ background: 'linear-gradient(135deg, #295CA8, #3D72B8)', boxShadow: '0 0 20px rgba(41,92,168,0.35)' }}>
                    {loading ? 'Se trimite...' : 'Trimite link de resetare'}
                  </button>
                </>
              )}

              <button type="button" onClick={() => { setMode('login'); setError(''); setSuccess(''); }}
                className="w-full flex items-center justify-center gap-1.5 text-xs text-white/30 hover:text-white/60 transition-colors py-1">
                <ArrowLeft size={12} /> Înapoi la autentificare
              </button>
            </form>
          )}
        </div>

        {/* Footer */}
        <div className="text-center mt-6 space-y-1">
          <p className="text-xs text-white/20">© {new Date().getFullYear()} INTERPERSONAL S.R.L. · RO34799221</p>
          <p className="text-[10px] text-white/12">Acces restricționat · Sistem de administrare intern</p>
        </div>
      </div>
    </div>
  );
}
